import { useQuery } from 'react-query'
import { ExternalIntegrationsClient } from 'api/clients/ExternalIntegrationsClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { PostLocation } from 'types'

class SearchLocationsError extends Error {
  errorType?: string

  constructor(message: string, errorType?: string) {
    super(message)
    this.errorType = errorType
  }
}

interface UseSearchLocationsProps {
  integrationId: number | undefined
  query: string
}

export const useSearchLocations = ({ integrationId, query }: UseSearchLocationsProps) => {
  const workspaceId = useCurrentWorkspaceId()

  const result = useQuery<PostLocation[], SearchLocationsError>(
    ['searchLocations', workspaceId, integrationId, query],
    async () => {
      if (!integrationId) return []

      const response = await ExternalIntegrationsClient.searchLocations({
        workspaceId,
        integrationId,
        query,
      })

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new SearchLocationsError(
          response.data?.error || 'Failed to search locations',
          response.data?.error_type,
        )
      }

      return response.data.locations
    },
    {
      enabled: !!workspaceId && !!integrationId && query.length >= 2,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 5,
      keepPreviousData: true,
      retry: false,
    },
  )

  return {
    ...result,
    isAuthError: result.error?.errorType === 'authentication_error',
  }
}
